import { LocaleBundle } from "../types";

const fr: LocaleBundle = {
  "ui": {
    "nav": {
      "features": "Fonctionnalités",
      "pricing": "Tarifs",
      "integrations": "Intégrations",
      "industries": "Secteurs d’activité",
      "apiDocs": "Documentation",
      "about": "À propos",
      "customers": "Clients",
      "contact": "Contact",
      "login": "Connexion",
      "getFreeCRM": "Obtenir FreeCRM",
      "openMenu": "Ouvrir le menu",
      "language": "Langue"
    },
    "hero": {
      "badge": "Le CRM multicanal : email, voix & SMS",
      "titleStart": "Le CRM conçu pour les ",
      "titleHighlight": "équipes de vente multicanales",
      "subtitle": "Atteignez chaque client là où il se trouve — avec l'email marketing, la voix et les SMS intégrés dans une seule plateforme. Pas de modules complémentaires, pas de prix d'entreprise.",
      "cta": "Commencez gratuitement — pas besoin de carte",
      "pills": [
        "Email marketing",
        "Voix et appels",
        "SMS bidirectionnels"
      ],
      "iosApp": "Application iOS",
      "androidApp": "Application Android",
      "heroAlt": "Tableau de bord FreeCRM affichant le pipeline des transactions et la gestion des contacts"
    },
    "logos": {
      "trustedBy": "Approuvé par plus de 25 000 équipes en croissance"
    },
    "features": {
      "eyebrow": "Fonctionnalités",
      "title": "Un seul CRM. Tous les canaux utilisés par vos clients.",
      "subtitle": "Email marketing, voix et SMS sur une seule plateforme — afin que votre équipe puisse joindre les prospects comme ils le préfèrent.",
      "items": [
        {
          "title": "Email marketing",
          "desc": "Concevez des campagnes, envoyez des diffusions et engagez les leads avec des séquences — le tout à partir du même enregistrement de contact."
        },
        {
          "title": "Voix et appels",
          "desc": "Appel en un clic, enregistrement des appels, et journalisation intégrés. Les appels entrants sont inclus avec messagerie vocale programmable — connectez votre numéro et démarrez des conversations instantanément."
        },
        {
          "title": "SMS bidirectionnels",
          "desc": "Envoyez des messages aux leads et aux clients depuis la même boîte de réception. Modèles, envois programmés et historique complet des conversations inclus."
        },
        {
          "title": "Séquences multicanales",
          "desc": "Mélangez email, SMS et appels dans une seule cadence. Atteignez chaque prospect sur le canal auquel il répond réellement."
        },
        {
          "title": "Analyse unifiée",
          "desc": "Suivez les ouvertures, clics, réponses, connexions et conversions sur chaque canal dans un seul tableau de bord."
        },
        {
          "title": "Automatisation",
          "desc": "Inscrivez automatiquement les leads aux campagnes, faites-les tourner entre les représentants et déclenchez des suivis basés sur n'importe quel signal."
        },
        {
          "title": "Planification de réunions",
          "desc": "Partagez des liens de réservation publics avec plusieurs types de réunion. La synchronisation du calendrier maintient vos disponibilités à jour et les liens de visioconférence sont générés automatiquement pour chaque réunion."
        },
        {
          "title": "Intégrations",
          "desc": "Connectez Google, Microsoft 365, DocuSign, QuickBooks, Zapier et la visioconférence avec Zoom, Google Meet et Zoho Meeting pour garder vos données synchronisées et automatiser les flux de travail dans les outils que vous utilisez déjà."
        },
        {
          "title": "CRM assisté par IA",
          "desc": "Laissez l'IA rédiger les réponses, suggérer les prochaines étapes, résumer les conversations et faire avancer chaque affaire — pour que votre équipe écrive moins et vende plus."
        }
      ]
    },
    "testimonials": {
      "eyebrow": "Clients",
      "title": "Approuvé par les équipes de vente multicanales",
      "items": [
        {
          "quote": "Nous avons remplacé trois outils — email marketing, composeur et CRM — avec FreeCRM. Notre équipe gère désormais des campagnes par email, SMS et appels depuis un seul endroit.",
          "name": "Maya Chen",
          "role": "VP Ventes, B2B SaaS"
        },
        {
          "quote": "Les séquences multicanales ont tout changé. Mélanger email, SMS et un appel dans une seule cadence a doublé notre taux de réponse sans augmenter les effectifs.",
          "name": "Daniel Okafor",
          "role": "Directeur des ventes, Apex Solutions"
        },
        {
          "quote": "Nous sommes passés de HubSpot à FreeCRM et avons enfin obtenu l'email marketing, la voix et les SMS sur une seule plateforme — sans frais d'intégration.",
          "name": "Priya Raman",
          "role": "COO, Brightline"
        }
      ]
    },
    "pricing": {
      "eyebrow": "Tarifs",
      "title": "Tarifs des logiciels CRM",
      "subtitle": "Numéros de téléphone américains seulement 1,15 $/mois · 0,01 $ par minute · 0,01 $ par SMS",
      "mostPopular": "Le plus populaire",
      "perUserMo": "/utilisateur/mois",
      "tiers": [
        {
          "name": "Gratuit",
          "price": "0 $",
          "blurb": "Gratuit pour toujours avec email, voix & SMS intégrés.",
          "cta": "S'inscrire gratuitement"
        },
        {
          "name": "Pro",
          "price": "10 $",
          "blurb": "Toute la puissance du CRM pour les équipes de vente en croissance.",
          "cta": "S'inscrire"
        },
        {
          "name": "Enterprise",
          "price": "25 $",
          "blurb": "Fonctionnalités avancées pour les équipes à fort volume.",
          "cta": "S'inscrire"
        }
      ],
      "sharedFeatures": [
        "Stockage illimité",
        "Contacts & entreprises",
        "Affaires & tâches",
        "Cas & appels",
        "Calendrier & documents",
        "Rapports",
        "Applications iOS & Android",
        "Campagnes & formulaires",
        "Email",
        "Accès API",
        "Synchronisation Google",
        "QuickBooks",
        "SMS",
        "Appels vocaux",
        "Carte de crédit"
      ]
    },
    "faq": {
      "eyebrow": "FAQ",
      "title": "Questions, réponses",
      "items": [
        {
          "q": "FreeCRM est-il vraiment gratuit pour l'email, la voix et les SMS ?",
          "a": "Oui — le plan Gratuit inclut l'email marketing, l'appel en un clic, la journalisation des appels et 500 SMS par mois sans frais. Les plans payants débloquent des limites d'envoi plus importantes, une automatisation avancée, l'enregistrement des appels et des SMS illimités."
        },
        {
          "q": "En quoi FreeCRM est-il différent de HubSpot ?",
          "a": "Contrairement à HubSpot, chaque canal est natif — l'email marketing, la voix et les SMS sont tous inclus, non vendus comme des modules complémentaires séparés ou des intégrations coûteuses."
        },
        {
          "q": "Puis-je exécuter des campagnes et des séquences d'email ?",
          "a": "Oui. Concevez des diffusions, des campagnes goutte à goutte et des séquences multi-étapes qui mélangent email, SMS et appels — avec un suivi complet des ouvertures, clics et réponses."
        },
        {
          "q": "Comment fonctionne la téléphonie dans FreeCRM ?",
          "a": "Les appels se passent directement dans votre navigateur — aucun composeur ou téléphone séparé requis. Passez des appels sortants en un clic, recevez des appels entrants sur votre numéro FreeCRM et routez-les vers le bon commercial. La messagerie vocale programmable avec salutations personnalisées et transcription automatique capture chaque appel manqué."
        },
        {
          "q": "Prenez-vous en charge les SMS bidirectionnels ?",
          "a": "Oui. Envoyez et recevez des SMS depuis la même boîte de réception que votre email, avec des modèles, des envois programmés et un historique complet des conversations par contact."
        }
      ]
    },
    "cta": {
      "title1": "Email, voix et SMS dans un seul CRM.",
      "title2": "Atteignez les clients sur tous les canaux.",
      "subtitle": "Rejoignez plus de 25 000 équipes utilisant FreeCRM pour l'email marketing, la voix et les SMS — le tout dans une plateforme multicanal gratuite.",
      "button": "Commencer gratuitement"
    },
    "footer": {
      "tagline": "Le CRM gratuit qui grandit avec votre entreprise.",
      "copyright": "Tous droits réservés.",
      "tagBuiltFor": "Conçu pour les équipes qui méritent mieux que HubSpot.",
      "cols": {
        "product": "Produit",
        "company": "Entreprise",
        "resources": "Ressources",
        "legal": "Légal"
      },
      "links": {
        "features": "Fonctionnalités",
        "pricing": "Tarifs",
        "integrations": "Intégrations",
        "industries": "Secteurs d’activité",
        "changelog": "Journal des modifications",
        "about": "À propos",
        "customers": "Clients",
        "careers": "Carrières",
        "contact": "Contact",
        "blog": "Blog",
        "helpCenter": "Centre d'aide",
        "apiDocs": "Documentation",
        "community": "Communauté",
        "privacy": "Confidentialité",
        "terms": "Conditions",
        "security": "Sécurité",
        "dpa": "ACD"
      }
    },
    "contentPage": {
      "readyToStart": "Prêt à commencer ?",
      "joinThousands": "Rejoignez des milliers d'équipes utilisant FreeCRM pour alimenter l'email marketing, la voix, les SMS et le pipeline.",
      "defaultCta": "Commencez gratuitement — pas besoin de carte",
      "learnMore": "En savoir plus →"
    },
    "languageNames": {
      "en": "English",
      "es": "Español",
      "fr": "Français",
      "pt": "Português",
      "it": "Italiano", "de": "Deutsch", "ar": "العربية"
    }
  },
  "featurePages": {
    "calling": {
      "title": "Appels intégrés",
      "eyebrow": "Fonctionnalité",
      "description": "Appel en un clic, appels entrants avec messagerie vocale programmable, appels par navigateur, numéros virtuels, enregistrement et composeur automatique — tout est intégré dans FreeCRM.",
      "intro": "Les solutions CRM de centre d'appels de FreeCRM automatisent les appels entrants et sortants dans 90 pays. Les appels entrants sont inclus avec messagerie vocale programmable — obtenez un numéro virtuel ou utilisez votre propre mobile, et commencez à appeler depuis n'importe quel enregistrement de contact.",
      "sections": [
        {
          "heading": "Appel en un clic depuis n'importe où",
          "body": "Découvrez pourquoi nous sommes le CRM gratuit n°1 pour l'automatisation de la force de vente, le marketing et le service client — avec des appels par navigateur en un clic vers n'importe quel numéro de téléphone, depuis n'importe quel enregistrement de contact, deal ou entreprise."
        },
        {
          "heading": "Enregistrement, planification & appels de groupe",
          "body": "Enregistrez les appels pour le coaching et la conformité, planifiez des suivis, organisez des appels de groupe et envoyez des SMS — le tout depuis le CRM sans outils supplémentaires."
        },
        {
          "heading": "Appels entrants et messagerie vocale programmable",
          "body": "Chaque numéro FreeCRM reçoit les appels entrants comme sortants — sans frais supplémentaires. Acheminez les appels vers le bon commercial, définissez les horaires d'ouverture et configurez une messagerie vocale programmable avec salutations personnalisées et transcription automatique pour ne jamais perdre un message."
        },
        {
          "heading": "Composeur automatique & script d'appel",
          "body": "Maximisez l'efficacité avec les clients existants et les prospects. Le script d'appel puissant et l'automatisation génèrent des résultats de vente fiables, vous aidant à augmenter vos bénéfices."
        },
        {
          "heading": "Campagnes & suivis",
          "body": "Créez des campagnes d'appels et de SMS, automatisez les appels et les SMS, et organisez et planifiez vos suivis afin que rien ne passe à travers les mailles du filet."
        }
      ]
    },
    "contacts": {
      "title": "Gestion des contacts & leads",
      "eyebrow": "Fonctionnalité",
      "description": "Contacts illimités, champs personnalisés et importation puissante depuis Outlook, Salesforce, HubSpot ou Zoho.",
      "intro": "Les contacts sont vos prospects, leads et clients — vendeurs, partenaires, donateurs. Dans FreeCRM, vos contacts sont de l'or et nous les traitons comme tels.",
      "sections": [
        {
          "heading": "Personnalisez tout",
          "body": "Ajoutez autant de champs personnalisés que vous le souhaitez. Chaque entreprise a besoin d'un CRM personnalisé — avec FreeCRM, vous pouvez personnaliser les fiches de contact, les formulaires et chaque partie du système."
        },
        {
          "heading": "Suivez chaque interaction",
          "body": "Suivez et gérez les relations, les appels et les réunions. Associez des documents, des notes de réunion et d'appel, et d'autres informations importantes aux personnes pour qui et avec qui vous travaillez."
        },
        {
          "heading": "B2B, B2C et plus",
          "body": "Les contacts sont des individus qui peuvent appartenir à des entreprises ou d'autres organisations. Nous prenons en charge le B2B, le B2C et des modèles commerciaux plus complexes."
        },
        {
          "heading": "Importez de n'importe où",
          "body": "Importez des listes de contacts depuis n'importe quel fichier tableur, Outlook, Salesforce, HubSpot, Zoho ou tout autre logiciel de gestion de contacts."
        }
      ]
    },
    "companies": {
      "title": "Entreprises & comptes",
      "eyebrow": "Fonctionnalité",
      "description": "Suivez les organisations, les fournisseurs et les partenaires avec des champs personnalisés, des listes et un historique d'activité partagé.",
      "intro": "La liste des Entreprises de FreeCRM offre un suivi organisationnel robuste et flexible pour toute entité — B2B, B2C, à but non lucratif ou gouvernementale — avec des champs entièrement personnalisables.",
      "sections": [
        {
          "heading": "Organiser et partager",
          "body": "Organisez par catégorie, statut, priorité ou filtres personnalisés. Importez et exportez instantanément des listes, créez des listes illimitées et partagez les informations organisationnelles au sein de votre équipe."
        },
        {
          "heading": "Toutes les activités en un seul endroit",
          "body": "Les appels de vente, les tickets d'incidents et les activités clients sont suivis et rapportés. Les campagnes d'e-mailing et le suivi de l'automatisation des appels sont organisés par entreprise."
        },
        {
          "heading": "Pourquoi des entreprises ?",
          "body": "En suivant les entreprises avec lesquelles vous faites des affaires, vos équipes peuvent orchestrer des campagnes de vente plus efficaces et identifier et partager des opportunités au sein de votre organisation."
        }
      ]
    },
    "deals": {
      "title": "Pipeline de deals",
      "eyebrow": "Fonctionnalité",
      "description": "Pipeline de vente visuel, suivi des étapes, prévisions et rapports — conçu pour les équipes de vente sortantes.",
      "intro": "Les deals sont la raison de votre présence ici. L'ensemble des fonctionnalités de deals de FreeCRM vous permet de gérer et de suivre les leads, en appliquant une méthodologie de vente solide pour faire passer votre entreprise au niveau supérieur.",
      "sections": [
        {
          "heading": "Suivi des étapes",
          "body": "Les deals sont suivis à travers leur statut — devis, estimation, bon de commande, facture, commission — afin que vous sachiez toujours où en est chaque opportunité."
        },
        {
          "heading": "Rapports & prévisions",
          "body": "Le pipeline fournit des tableaux, des rapports et des graphiques montrant le rendement de votre équipe de vente et de vos campagnes. Quantifiez le succès au fil du temps et identifiez de nouvelles opportunités chez les clients nouveaux ou existants."
        },
        {
          "heading": "Collaboration d'équipe",
          "body": "Le suivi des deals apporte de la cohésion aux personnes qui soutiennent vos ventes. Les services et le support peuvent collaborer tout au long du cycle pour démontrer et assurer le succès de l'équipe."
        }
      ]
    },
    "tasks": {
      "title": "Gestion des tâches",
      "eyebrow": "Fonctionnalité",
      "description": "Priorisez, attribuez et suivez les tâches avec workflow et statut — à travers les deals, les contacts et les cas.",
      "intro": "Le suivi des tâches, des problèmes et des ventes vous offre une visibilité complète sur le temps que vous et votre équipe consacrez à chaque deal ou problème.",
      "sections": [
        {
          "heading": "Exécuter la tâche !",
          "body": "Priorisez les tâches, créez des types de tâches personnalisés spécifiques à votre entreprise, attribuez des tâches aux membres de l'équipe ou aux employés, donnez-leur un workflow et un statut, et déléguez la charge de travail au sein de votre équipe."
        },
        {
          "heading": "Un meilleur délégateur",
          "body": "En attribuant des tâches, vous devenez instantanément un meilleur délégateur sans perdre de vue la qualité et le service offerts par votre équipe."
        }
      ]
    },
    "cases": {
      "title": "Gestion des cas",
      "eyebrow": "Fonctionnalité",
      "description": "Ouvrez des tickets, acheminez les problèmes, personnalisez les statuts et les priorités, et résolvez chaque cas client.",
      "intro": "Le suivi des cas et des problèmes offre une visibilité à 100 % sur les problèmes que vous traitez pour les clients, les fournisseurs et les partenaires.",
      "sections": [
        {
          "heading": "Service d'assistance virtuelle",
          "body": "Ouvrez un nouveau cas et un ticket dans votre service d'assistance virtuelle. Associez les cas à long terme aux problèmes en cours qui peuvent être rapidement résolus et clôturés."
        },
        {
          "heading": "Personnaliser pour votre équipe",
          "body": "Personnalisez les problèmes de support avec des champs personnalisés, attribuez les problèmes à un ou plusieurs membres de l'équipe, et suivez chaque cas pour qu'aucun problème client ne soit négligé."
        },
        {
          "heading": "Connecter tout",
          "body": "Reliez les cas aux entreprises, aux contacts, aux appels, aux événements ou aux documents. Ajoutez des notes au fur et à mesure que le problème progresse et conservez toutes les informations dans un seul cas pratique."
        }
      ]
    },
    "campaigns": {
      "title": "Automatisation du marketing",
      "eyebrow": "Fonctionnalité",
      "description": "Campagnes email et SMS, segmentation et automatisation intégrées à votre CRM.",
      "intro": "Créez des listes de diffusion à partir de données démographiques, de préférences et d'interactions. Créez et exécutez des campagnes email personnalisées en quatre étapes simples.",
      "sections": [
        {
          "heading": "Personnalisé à grande échelle",
          "body": "Le logiciel CRM d'email marketing fait plus qu'envoyer des emails en masse — construisez des séquences personnalisées, gérez des listes ciblées et effectuez un suivi automatique."
        },
        {
          "heading": "Modèles mobiles-prêts",
          "body": "Modèles d'e-mail facilement personnalisables et réactifs sur tous les appareils, y compris les téléphones mobiles, les tablettes et plus encore."
        },
        {
          "heading": "SMS + email ensemble",
          "body": "Combinez les séquences d'emails et de SMS. Engagez vos clients comme jamais auparavant et gardez tout au même endroit."
        }
      ]
    },
    "calendar": {
      "title": "Calendrier de groupe",
      "eyebrow": "Fonctionnalité",
      "description": "Calendrier d'équipe partagé avec rappels par e-mail et SMS, entièrement intégré aux contacts, aux transactions et aux tâches.",
      "intro": "Le calendrier de groupe FreeCRM est un excellent moyen de gérer et de visualiser votre emploi du temps, les activités quotidiennes, hebdomadaires et mensuelles de votre équipe, les réunions clients et les appels.",
      "sections": [
        {
          "heading": "Ne manquez jamais un événement",
          "body": "Envoyez des notifications par e-mail et SMS afin que personne n'ait plus d'excuse pour manquer un événement."
        },
        {
          "heading": "Connecté à tout",
          "body": "Suivez les événements à venir, définissez des rappels, créez des tâches, saisissez des notes, joignez des fichiers et associez les entrées de calendrier à un ou plusieurs contacts. Attribuez des tâches à un ou plusieurs collègues."
        },
        {
          "heading": "Filtrer par équipe ou contact",
          "body": "Affichez les événements pour l'ensemble de la base de données, un seul contact, vous-même, un collègue ou plusieurs membres de l'équipe. Vous n'êtes jamais à plus d'un clic de l'historique ou des informations de contact."
        }
      ]
    },
    "documents": {
      "title": "Gestion de documents",
      "eyebrow": "Fonctionnalité",
      "description": "Stockage de documents illimité, accès mobile et pièces jointes par enregistrement.",
      "intro": "Partagez des documents importants avec votre équipe et offrez un accès instantané à tous vos fichiers électroniques. Téléchargez un seul fichier ou un répertoire entier en un clic.",
      "sections": [
        {
          "heading": "Stockage illimité",
          "body": "Notre service gratuit inclut un stockage illimité pour tous les documents importants de votre équipe. Chaque membre peut avoir son propre espace de stockage et lier des fichiers à n'importe quel enregistrement."
        },
        {
          "heading": "Priorité au mobile",
          "body": "Accédez aux documents depuis nos applications iOS et Android – ouvrez le CRM mobile, affichez un document, et envoyez des SMS et des e-mails aux prospects directement sur place."
        },
        {
          "heading": "Centraliser les actifs",
          "body": "Un endroit sûr et sécurisé pour centraliser vos actifs documentaires électroniques. Votre équipe peut accéder aux contrats, à la littérature de vente et de marketing, aux formulaires standardisés, et plus encore, depuis n'importe où dans le monde."
        }
      ]
    },
    "email": {
      "title": "Automatisation des e-mails",
      "eyebrow": "Fonctionnalité",
      "description": "Séquences d'e-mails personnalisées, listes segmentées et ciblage basé sur le CRM.",
      "intro": "Créez des listes de diffusion basées sur les données démographiques, les préférences et les interactions pour des messages ciblés. Gagnez du temps et augmentez votre productivité de 74 % avec les bons outils d'e-mail.",
      "sections": [
        {
          "heading": "Quatre étapes simples",
          "body": "Créez et exécutez des campagnes d'e-mails personnalisées en quatre étapes simples avec le logiciel CRM d'e-mail marketing."
        },
        {
          "heading": "Plus que du volume",
          "body": "Le marketing par e-mail CRM aide les équipes bien plus que pour le suivi des leads de vente, l'envoi d'e-mails en masse ou la création de séquences personnalisées."
        },
        {
          "heading": "Modèles réactifs",
          "body": "Des modèles facilement personnalisables et réactifs sur tous les appareils — téléphones, tablettes et ordinateurs de bureau."
        }
      ]
    },
    "forms": {
      "title": "Formulaires & sondages",
      "eyebrow": "Fonctionnalité",
      "description": "Formulaires d'assistant multipages, scripts d'appel et sondages qui alimentent directement le CRM.",
      "intro": "Les formulaires sont des enquêtes de questions-réponses — et ils peuvent aussi être des scripts d'appel que votre personnel de vente ou de support complète avec les prospects en ligne.",
      "sections": [
        {
          "heading": "Formulaires assistants",
          "body": "La création de formulaires et de sondages est facile avec notre constructeur de formulaires, créant des formulaires assistants multipages faciles à suivre et à compléter. Une fois terminés, vous recevrez un lien public à distribuer."
        },
        {
          "heading": "Retour d'expérience client & scripts internes",
          "body": "Utilisez des formulaires pour le feedback client, les questionnaires de personnel, et les scripts d'appel. Créez des sondages personnalisés pour les ventes, le marketing et la satisfaction client."
        },
        {
          "heading": "Rapports & export",
          "body": "Les résultats des formulaires web et des sondages alimentent un système de reporting, présenté sous forme de tableaux. Exportez vers Excel ou CSV pour l'analyse ou l'importation marketing."
        }
      ]
    }
  },
  "industryPages": {
    "callcenter": {
      "title": "FreeCRM pour les centres d'appels",
      "eyebrow": "Secteur d'activité",
      "description": "CRM de centre d'appels avec numérotation, enregistrement, script et SMS intégrés dans 90 pays.",
      "intro": "Automatisez les appels entrants et sortants dans 90 pays du monde entier. Obtenez un numéro de téléphone virtuel et configurez les fonctionnalités de centre d'appels dès le départ.",
      "sections": [
        {
          "heading": "Tous vos appels, au même endroit",
          "body": "Appels par navigateur en un clic vers n'importe quel numéro de téléphone. Obtenez un numéro virtuel ou utilisez votre propre mobile — FreeCRM fait tout."
        },
        {
          "heading": "Enregistrement & planification",
          "body": "Faites des enregistrements d'appels, planifiez des appels et des appels de groupe, et envoyez des SMS depuis n'importe quel appareil. Fonctionnalités avancées pour l'automatisation des centres d'appels."
        },
        {
          "heading": "Scripting & automatisation",
          "body": "La puissant écriture de script d'appel et l'automatisation génèrent des résultats de vente fiables. Créez des scripts assignés et planifiés aux membres de l'équipe ; suivez chaque appel et ses résultats dans notre système riche en fonctionnalités."
        },
        {
          "heading": "Campagnes & suivi",
          "body": "Créez des campagnes d'appels et de SMS, automatisez-les et organisez les suivis afin que votre équipe ne manque jamais un point de contact."
        }
      ]
    },
    "insurance": {
      "title": "FreeCRM pour l'assurance",
      "eyebrow": "Secteur d'activité",
      "description": "CRM cloud d'assurance pour les agents, les souscripteurs et les services afin de suivre les clients, les polices et les sinistres.",
      "intro": "Le CRM de l'industrie de l'assurance aide les agents, les souscripteurs et les services à vendre aux consommateurs et aux entreprises et à suivre toutes les interactions dans le cloud.",
      "sections": [
        {
          "heading": "Conçu pour les agents et les souscripteurs",
          "body": "Que vous soyez un agent, un souscripteur, un fournisseur de services ou un autre professionnel de l'assurance, nous pouvons vous aider avec vos besoins en CRM."
        },
        {
          "heading": "Polices, sinistres & clients",
          "body": "Automatisez le suivi des clients importants, des polices, des sinistres et plus encore avec des modèles d'assurance prêts à l'emploi qui fonctionnent pour le B2C et le B2B."
        },
        {
          "heading": "Conçu pour la rétention",
          "body": "Le CRM est essentiel dans l'assurance en raison de la complexité des produits, de la nécessité d'une communication continue et de l'importance de la rétention des clients."
        }
      ]
    },
    "realestate": {
      "title": "FreeCRM pour l'immobilier",
      "eyebrow": "Secteur d'activité",
      "description": "Gestion des leads et des biens immobiliers pour les professionnels de l'immobilier résidentiel et commercial.",
      "intro": "FreeCRM est utilisé dans l'ensemble du secteur immobilier par toutes les catégories de professionnels — résidentiel ou commercial, achat, vente, courtage ou gestion.",
      "sections": [
        {
          "heading": "Gestion des leads et des propriétés",
          "body": "Rationalisez la gestion des leads et des propriétés dans le résidentiel et le commercial. Les agents aiment notre CRM — tout comme les courtiers et les gestionnaires immobiliers."
        },
        {
          "heading": "Listez les propriétés comme des deals",
          "body": "Utilisez la section Deals pour lister les propriétés — généralement par adresse (par exemple, '100 Main Street'). Ajoutez plusieurs prospects à une seule propriété et utilisez le tableau de ventes ou le pipeline pour suivre les entonnoirs."
        },
        {
          "heading": "Associations de propriétaires (HOAs) et actifs complexes",
          "body": "Les gestionnaires immobiliers pour le résidentiel, le commercial, le multi-familial, les copropriétés de grande hauteur et les HOAs utilisent FreeCRM pour gérer chaque aspect des opérations."
        }
      ]
    },
    "dental": {
      "title": "FreeCRM pour les cabinets dentaires",
      "eyebrow": "Secteur d'activité",
      "description": "CRM prêt pour HIPAA pour les dentistes, les chirurgiens buccaux et la communauté dentaire spécialisée.",
      "intro": "Nous soutenons l'industrie dentaire avec un CRM de pointe pour les dentistes, les chirurgiens buccaux et tous les membres de la communauté médicale spécialisée — et fournissons une certification BAA pour la conformité HIPAA.",
      "sections": [
        {
          "heading": "Approuvé par des milliers de cabinets",
          "body": "Avec des milliers de clients dentaires, FreeCRM gère les exigences de conformité des cabinets dentaires, de la chirurgie buccale, des implants dentaires et des industries adjacentes qui traitent des informations sur les patients."
        },
        {
          "heading": "Sécurité des données",
          "body": "La sécurité des données est au cœur de FreeCRM. Les serveurs sont hébergés sur AWS — conforme HIPAA — et nous fournissons des certificats BAA aux cabinets qui en ont besoin."
        },
        {
          "heading": "Intégrez votre stack",
          "body": "Commencez rapidement et intégrez-vous aux principaux systèmes de gestion de cabinet et d'imagerie utilisés en dentisterie."
        }
      ]
    },
    "hipaa": {
      "title": "CRM conforme HIPAA",
      "eyebrow": "Conformité",
      "description": "FreeCRM est conforme HIPAA depuis plus d'une décennie — certificats BAA disponibles avec l'édition Entreprise.",
      "intro": "La gestion des informations de santé des patients est un défi. FreeCRM a lancé HIPAACRM.com en 2014 — le premier CRM au monde conforme HIPAA — et reste un leader en matière de sécurité et de responsabilité.",
      "sections": [
        {
          "heading": "Besoin d'un BAA ?",
          "body": "Si vous avez besoin d'un certificat d'avenant d'associé commercial (BAA) pour garantir la conformité HIPAA, nous pouvons vous aider. Notre édition Entreprise est requise, payée annuellement par utilisateur à l'avance (300 $/utilisateur/an)."
        },
        {
          "heading": "Au-delà des cabinets médicaux",
          "body": "Il ne s'agit pas seulement des cabinets médicaux — les essais cliniques, les nutritionnistes, les thérapeutes et toute entreprise traitant des informations de santé profitent de notre support HIPAA."
        },
        {
          "heading": "Sécurité de pointe",
          "body": "12 ans d'expérience HIPAA, hébergé sur AWS, avec la sécurité et la responsabilité dont vous avez besoin pour vos informations de santé protégées."
        }
      ]
    },
    "insidesales": {
      "title": "FreeCRM pour les vendeurs internes & indépendants",
      "eyebrow": "Secteur d'activité",
      "description": "Augmentez la productivité de plus de 30 % avec une automatisation CRM simple conçue pour les représentants internes et indépendants.",
      "intro": "FreeCRM aide les commerciaux indépendants et les équipes de vente internes à augmenter leur productivité grâce à des outils d'automatisation CRM simples et des tableaux de bord.",
      "sections": [
        {
          "heading": "Plus de 30 % de productivité supplémentaire",
          "body": "Les représentants commerciaux indépendants peuvent augmenter leurs ventes de plus de 30 % simplement en suivant les contacts et les entreprises — et nos outils d'automatisation amplifient l'impact."
        },
        {
          "heading": "Suivez chaque interaction",
          "body": "Enregistrez chaque appel, e-mail et réunion. Ayez un enregistrement clair de ce qui a été dit et de ce qui doit se passer ensuite, priorisez les tâches et suivez en temps voulu."
        },
        {
          "heading": "Adapté aux travailleurs indépendants",
          "body": "Conçu pour l'autonomie des représentants indépendants qui représentent plusieurs entreprises. Gérez votre propre emploi du temps et vos relations clients depuis n'importe où."
        }
      ]
    },
    "contractor": {
      "title": "FreeCRM pour les entrepreneurs et constructeurs",
      "eyebrow": "Secteur d'activité",
      "description": "Gérez les affaires, les sous-traitants, la planification et les alertes en temps réel pour les entrepreneurs généraux (CG) et les constructeurs commerciaux.",
      "intro": "Les entrepreneurs, constructeurs, CG et fournisseurs de bâtiments commerciaux utilisent facilement FreeCRM pour gérer les transactions et de multiples tiers comme les sous-traitants.",
      "sections": [
        {
          "heading": "Gestion des prospects",
          "body": "Suivez les prospects, surveillez les progrès et priorisez les efforts. Obtenez des informations sur le comportement des prospects afin de décider lesquels poursuivre."
        },
        {
          "heading": "Estimations & propositions",
          "body": "Automatisez le processus d'estimation et de proposition avec une base de données centralisée des propositions précédentes, des estimations et des données de prix — ce qui fait gagner du temps et améliore la précision."
        },
        {
          "heading": "Planification & alertes",
          "body": "Attribuez rapidement des équipes et des individus avec des alertes en temps réel par e-mail ou SMS. Coordonnez les sous-traitants et les équipes."
        }
      ]
    },
    "professional": {
      "title": "FreeCRM pour les services professionnels",
      "eyebrow": "Secteur d'activité",
      "description": "Gestion des clients, conformité et prestation de services pour les cabinets de services professionnels.",
      "intro": "Les cabinets de services professionnels utilisent FreeCRM pour gérer les données clients, suivre les interactions et fournir un meilleur service depuis n'importe où, sur n'importe quel appareil.",
      "sections": [
        {
          "heading": "Amélioration de la satisfaction client",
          "body": "Une vue complète de la relation client — coordonnées, transactions passées, préférences de communication — conduit à une satisfaction et une fidélité accrues."
        },
        {
          "heading": "Conformité facilitée",
          "body": "Satisfaire aux exigences du GDPR et similaires grâce à un emplacement central pour stocker et partager la documentation de conformité, et une transparence sur les sources de données clients."
        },
        {
          "heading": "Développez l'entreprise",
          "body": "Le CRM d'aujourd'hui doit être accessible de partout, sur n'importe quel appareil, dans n'importe quelle langue — avec des outils sociaux et des meilleures pratiques intégrées pour vous aider à avancer plus rapidement."
        }
      ]
    }
  },
  "integrationPages": {
    "google": {
      "title": "FreeCRM + Google Workspace",
      "eyebrow": "Intégration",
      "description": "Synchronisez Gmail, Google Calendar et Google Contacts avec FreeCRM en un clic.",
      "intro": "Gmail et Google Workspace sont profondément intégrés à FreeCRM. Connectez votre compte Google et synchronisez vos e-mails, contacts et calendrier.",
      "sections": [
        {
          "heading": "Configuration en un clic",
          "body": "Il est simple d'ajouter votre compte Google — comme toute autre application intégrée à Google, c'est une connexion en un clic. Vous pouvez également utiliser votre compte Google pour vous connecter à FreeCRM."
        },
        {
          "heading": "Synchronisez ce que vous voulez",
          "body": "Une fois que vous avez ajouté Google à FreeCRM, choisissez de synchroniser l'intégralité du compte ou simplement d'utiliser la fonctionnalité e-mail intégrée. Le calendrier et Google Contacts sont inclus lors de la synchronisation."
        },
        {
          "heading": "Workspace devient un CRM",
          "body": "Les systèmes CRM hautement intégrés peuvent transformer Google Workspace en un puissant CRM qui fonctionne avec l'hébergement Google d'entreprise ou n'importe quel compte Gmail."
        }
      ]
    },
    "quickbooks": {
      "title": "FreeCRM + QuickBooks",
      "eyebrow": "Intégration",
      "description": "L'intégration CRM QuickBooks la mieux notée — Online et Desktop, synchronisation bidirectionnelle.",
      "intro": "La meilleure intégration QuickBooks — classée n°1 par les ProAdvisors QuickBooks. Le CRM pour QuickBooks Online et Desktop simplifie les opérations et vous aide à développer vos ventes.",
      "sections": [
        {
          "heading": "Étroitement intégré",
          "body": "Si vous recherchez une application CRM étroitement intégrée à QuickBooks Online, FreeCRM est la solution parfaite."
        },
        {
          "heading": "Centralisez les données clients",
          "body": "Un CRM qui s'intègre à QuickBooks utilise les intégrations de logiciels de comptabilité pour stocker et centraliser vos données clients entre les ventes et les finances."
        },
        {
          "heading": "Online + Desktop",
          "body": "QuickBooks Online et QuickBooks Desktop sont tous deux pris en charge."
        }
      ]
    },
    "zapier": {
      "title": "FreeCRM + Zapier",
      "eyebrow": "Intégration",
      "description": "Connectez FreeCRM avec plus de 6 000 applications sur Zapier — le moyen le plus simple d'automatiser.",
      "intro": "Connectez instantanément le connecteur FreeCRM avec les applications que vous utilisez tous les jours. FreeCRM s'intègre avec plus de 6 000 autres applications sur Zapier — le moyen le plus simple d'automatiser.",
      "sections": [
        {
          "heading": "Plus de 6 000 applications",
          "body": "De Slack à Stripe en passant par Google Sheets, transférez et extrayez des données de FreeCRM vers le reste de votre pile sans écrire de code."
        },
        {
          "heading": "Automatisation sans code",
          "body": "Créez des Zaps pour enregistrer les appels, créer des contacts, synchroniser les transactions et déclencher des campagnes chaque fois qu'un événement se produit dans vos autres outils."
        }
      ]
    },
    "docusign": {
      "title": "FreeCRM + DocuSign",
      "eyebrow": "Intégration",
      "description": "Envoyez, signez et suivez les accords directement depuis votre CRM avec l'intégration DocuSign.",
      "intro": "DocuSign est la plateforme leader mondial pour la signature de documents. FreeCRM vous permet de rationaliser les processus en remplissant les documents à partir des données CRM et en mettant à jour le CRM à partir des champs DocuSign.",
      "sections": [
        {
          "heading": "Suivi en temps réel",
          "body": "Demandez des signatures et suivez le statut des accords en temps réel. Gagnez du temps avec des workflows personnalisés qui déclenchent des accords en un clic — et réduisez les erreurs."
        },
        {
          "heading": "Flux de données bidirectionnel",
          "body": "Automatisez des workflows entiers, générez et envoyez des contrats, et archivez les documents complétés. Mettez à jour les systèmes avec les données des documents ou remplissez les documents à partir du CRM — cela fonctionne dans les deux sens."
        }
      ]
    },
    "whatsapp": {
      "title": "FreeCRM + WhatsApp",
      "eyebrow": "Intégration",
      "description": "Utilisez l'API WhatsApp Business pour les appels et les messages texte depuis FreeCRM.",
      "intro": "WhatsApp et FreeCRM forment une combinaison puissante — utilisez l'API WhatsApp Business pour les appels et les messages texte, et automatisez la capture de leads et les communications.",
      "sections": [
        {
          "heading": "Communication simplifiée",
          "body": "Gérez les interactions clients WhatsApp en un seul endroit central pour des temps de réponse plus rapides et une satisfaction accrue."
        },
        {
          "heading": "Intégration des données clients",
          "body": "Intégrez les données clients WhatsApp dans FreeCRM et automatisez les tâches répétitives comme le routage des messages, la priorisation des réponses et la saisie de données."
        }
      ]
    },
    "microsoft365": {
      "title": "FreeCRM + Microsoft 365",
      "eyebrow": "Intégration",
      "description": "Synchronisez Outlook, le calendrier et les contacts avec FreeCRM grâce à Microsoft 365.",
      "intro": "Microsoft 365 s'intègre à FreeCRM pour synchroniser vos e-mails Outlook, votre calendrier et vos contacts au même endroit.",
      "sections": [
        {
          "heading": "Connexion Microsoft en un clic",
          "body": "Connectez votre compte Microsoft 365 et utilisez-le pour vous connecter à FreeCRM, comme n'importe quelle autre application intégrée à Microsoft."
        },
        {
          "heading": "E-mails, calendrier et contacts",
          "body": "Choisissez ce que vous souhaitez synchroniser : les conversations par e-mail, les événements du calendrier et les contacts Outlook restent à jour entre FreeCRM et Microsoft 365."
        },
        {
          "heading": "Prêt pour les équipes",
          "body": "Compatible avec Outlook personnel, Microsoft 365 Business et les locataires Enterprise afin que toute votre équipe reste synchronisée."
        }
      ]
    },
    "video-conferencing": {
      "title": "FreeCRM + Visioconférence",
      "eyebrow": "Intégration",
      "description": "Générez automatiquement des liens Zoom, Google Meet et Zoho Meeting pour chaque réunion planifiée.",
      "intro": "La planification FreeCRM crée automatiquement des liens de visioconférence pour chaque réunion. Connectez Zoom, Google Meet et Zoho Meeting pour que prospects et clients rejoignent en un clic.",
      "sections": [
        {
          "heading": "Liens de réunion automatiques",
          "body": "Lorsqu'une personne réserve une réunion via votre lien FreeCRM, un lien Zoom, Google Meet ou Zoho Meeting est généré et inséré automatiquement dans l'invitation du calendrier."
        },
        {
          "heading": "Fournisseurs multiples",
          "body": "Utilisez le fournisseur déjà payé par votre équipe — Zoom, Google Meet ou Zoho Meeting — ou laissez les participants choisir."
        },
        {
          "heading": "Calendrier unifié",
          "body": "Les détails de la réunion, les liens et les enregistrements restent attachés à la fiche contact, affaire ou cas pour que tout soit au même endroit."
        }
      ]
    },
    "aws": {
      "title": "FreeCRM sur AWS",
      "eyebrow": "Infrastructure",
      "description": "Disponibilité à six neuf, ingénieurs 24/7/365 et sécurité des données de niveau fédéral sur Amazon AWS.",
      "intro": "FreeCRM fonctionne sur Amazon AWS avec un objectif de disponibilité de six neufs (99,9999 %). Les ingénieurs sont en service 24h/24, 7j/7, 365 jours par an.",
      "sections": [
        {
          "heading": "Installations de niveau fédéral",
          "body": "Nos installations physiques respectent ou dépassent toutes les exigences fédérales et internationales en matière de sécurité des données, de redondance, de reprise d’activité et de continuité."
        },
        {
          "heading": "Cloud de la plus haute qualité",
          "body": "Faites-nous confiance pour fournir les services cloud de la plus haute qualité disponibles partout — chiffrés, surveillés et continuellement disponibles."
        }
      ]
    }
  },
  "footerPages": {
    "features": {
      "title": "Tout ce dont vous avez besoin dans un seul CRM",
      "eyebrow": "Fonctionnalités",
      "description": "Appels, SMS, affaires, contacts, tâches, dossiers, campagnes, calendrier, documents, rapports, API et intégrations intégrés — gratuits pour toujours.",
      "intro": "FreeCRM vous offre des contacts, des entreprises, des affaires, des tâches, des cas, des appels, un calendrier, des documents, des rapports, des campagnes, des formulaires, des e-mails, une API et des intégrations — le tout au même endroit, gratuitement pour toujours."
    },
    "pricing": {
      "title": "Tarifs",
      "description": "CRM gratuit pour toujours. Pro à 10 $/utilisateur/mois. Entreprise à 25 $/utilisateur/mois. Numéros US 1,15 $/mois, 0,01 $/min, 0,01 $/SMS."
    },
    "integrations": {
      "title": "Intégrations & applications connectées",
      "eyebrow": "Intégrations",
      "description": "Connectez FreeCRM avec Google, Microsoft 365, QuickBooks, Zapier, DocuSign, WhatsApp, Zoom, Google Meet, Zoho Meeting et plus de 6 000 applications.",
      "intro": "FreeCRM se connecte aux outils que vous utilisez déjà — Google Workspace, Microsoft 365, QuickBooks, Zapier, DocuSign, WhatsApp, Zoom, Google Meet, Zoho Meeting et des milliers d'autres — pour maintenir la synchronisation de vos données et l'automatisation de vos flux de travail."
    },
    "changelog": {
      "title": "Nouveautés dans FreeCRM",
      "eyebrow": "Journal des modifications",
      "description": "Améliorations récentes, nouvelles fonctionnalités et corrections expédiées à FreeCRM.",
      "intro": "Nous déployons des améliorations en continu. Voici les points forts des dernières versions.",
      "sections": [
        {
          "heading": "Mai 2026 — Power dialer 2.0",
          "body": "Numérotation de présence locale plus rapide, messagerie vocale plus intelligente et raccourcis de disposition d'appel qui réduisent de moitié le travail après appel."
        },
        {
          "heading": "Avril 2026 — Nouvelles applications iOS et Android",
          "body": "De toutes nouvelles applications mobiles avec support hors ligne, notifications push pour les appels entrants et les SMS, et accès complet au pipeline depuis votre téléphone."
        },
        {
          "heading": "Mars 2026 — Synchronisation bidirectionnelle QuickBooks",
          "body": "Clients, factures et paiements se synchronisent désormais de manière bidirectionnelle entre FreeCRM et QuickBooks Online et Desktop."
        },
        {
          "heading": "Février 2026 — Coaching d'appels",
          "body": "Écoute en direct, murmure et intervention pour les managers, ainsi qu'une notation des appels générée par l'IA sur Enterprise."
        }
      ]
    },
    "about": {
      "title": "À propos de FreeCRM",
      "eyebrow": "Entreprise",
      "description": "Depuis 2003, FreeCRM a proposé le premier CRM entièrement gratuit au monde, pionnier du modèle freemium pour les équipes de vente, de service et de marketing.",
      "intro": "Pourquoi FreeCRM est-il gratuit ? Parce que nous croyons en votre entreprise. Nous proposons le premier CRM entièrement gratuit au monde depuis 2003 — et nous avons aidé des centaines de milliers d'entreprises à se développer.",
      "sections": [
        {
          "heading": "Pourquoi gratuit ?",
          "body": "Nous vous offrons un CRM gratuit comme un investissement en vous et votre entreprise. Nous savons que nous vous aiderons à réussir et qu'à l'avenir, vous pourriez nous payer. Au fur et à mesure que vos ventes augmentent, vous voudrez plus de fonctionnalités — et Pro et Enterprise seront là quand vous serez prêt."
        },
        {
          "heading": "Nous avons été les pionniers du freemium",
          "body": "FreeCRM a été le pionnier du modèle freemium : un logiciel puissant gratuit, avec la possibilité de passer à une version supérieure pour les SMS, les appels vocaux, QuickBooks, l'accès API avancé, Zapier, et plus encore."
        },
        {
          "heading": "Construit sur AWS",
          "body": "Nous prenons la sécurité des données au sérieux. FreeCRM fonctionne sur Amazon AWS avec un objectif de disponibilité de six neufs (99,9999 %). Les ingénieurs sont en service 24h/24, 7j/7, 365 jours par an, et nos installations respectent ou dépassent les exigences fédérales et internationales en matière de sécurité des données, de redondance et de continuité."
        },
        {
          "heading": "Conception exhaustive",
          "body": "Un CRM robuste est une application de travail complète qui gère les opérations front-office et back-office. FreeCRM est aussi puissant — et gratuit — couvrant les ventes, le service, le marketing, les appels et les opérations en un seul endroit."
        }
      ]
    },
    "customers": {
      "title": "Les clients adorent FreeCRM",
      "eyebrow": "Clients",
      "description": "Découvrez comment les équipes de vente sortantes, les centres d'appels et les PME utilisent FreeCRM pour effectuer 40 % d'appels en plus et conclure des affaires plus rapidement.",
      "intro": "Des centaines de milliers d'équipes — des startups de deux personnes aux centres d'appels de 50 postes — gèrent leurs ventes et services sur FreeCRM.",
      "sections": [
        {
          "heading": "Ventes internes — 40 % d'appels en plus",
          "body": "« Nos SDR sont passés de 80 appels par jour à plus de 110 après être passés à FreeCRM. Le composeur automatique et l'appel en un clic dans la vue contact ont tout changé. » — VP Ventes, SaaS B2B"
        },
        {
          "heading": "Centres d'appels — ont remplacé une pile de 18 000 $/an",
          "body": "« Nous avons remplacé nos outils de numérotation, CRM et SMS par FreeCRM et avons économisé 18 000 $ par an — sans perdre une seule fonctionnalité que nos représentants appréciaient. » — Directeur des opérations, Assurance"
        },
        {
          "heading": "PME — migration de 50 postes",
          "body": "« Nous avons déployé FreeCRM pour 50 agents en deux semaines. La mise en service de numéros locaux et l'enregistrement des appels étaient opérationnels dès le premier jour. » — Responsable des revenus, Services à domicile"
        },
        {
          "heading": "Organisation à but non lucratif — gestion des donateurs",
          "body": "« FreeCRM nous permet de traiter les donateurs comme des contacts et les engagements comme des transactions. Le prix est juste et les fonctionnalités d'appel changent la donne. » — Directeur exécutif, NPO"
        }
      ]
    },
    "careers": {
      "title": "Carrières chez FreeCRM",
      "eyebrow": "Carrières",
      "description": "Rejoignez une équipe axée sur le travail à distance, créant le CRM gratuit le plus populaire au monde avec appels et SMS intégrés.",
      "intro": "Nous sommes une équipe axée sur le travail à distance, créant le CRM gratuit le plus populaire au monde. Si vous aimez les logiciels simples, l'itération rapide et les clients qui utilisent réellement ce que vous construisez, nous serions ravis de discuter.",
      "sections": [
        {
          "heading": "Notre mode de travail",
          "body": "Petites équipes, priorité à l'asynchrone, priorité à l'écrit. Nous livrons chaque semaine. Nous embauchons des seniors, faisons confiance aux gens pour prendre en charge leur travail et offrons une rémunération compétitive en espèces et en capital."
        },
        {
          "heading": "Postes ouverts",
          "body": ""
        },
        {
          "heading": "Comment postuler",
          "body": "Envoyez un e-mail à careers@freecrm.com avec le poste qui vous intéresse, une brève note expliquant pourquoi, et des liens vers votre travail. Nous répondons à toutes les candidatures."
        }
      ],
      "roles": [
        "Ingénieur Full-stack Senior (React / Node)",
        "Ingénieur Téléphonie (WebRTC, SIP)",
        "Concepteur de produits",
        "Succès client — Vente sortante",
        "Responsable de compte — PME"
      ]
    },
    "contact": {
      "title": "Contacter FreeCRM",
      "eyebrow": "Contact",
      "description": "Parlez aux ventes, obtenez du support produit ou contactez nos équipes confidentialité et sécurité.",
      "intro": "Nous sommes là pour vous aider. Contactez la bonne équipe ci-dessous — nous répondons généralement dans un délai d'un jour ouvrable.",
      "sections": [
        {
          "heading": "Ventes",
          "body": "Vous souhaitez une démonstration de Pro ou Enterprise, ou de l'aide pour transférer des numéros et migrer depuis HubSpot ? Envoyez un e-mail à sales@freecrm.com."
        },
        {
          "heading": "Support",
          "body": "Client existant ? Ouvrez un ticket depuis l'application ou envoyez un e-mail à support@freecrm.com. Les clients Pro et Enterprise bénéficient d'une réponse prioritaire."
        },
        {
          "heading": "Confidentialité & sécurité",
          "body": "Questions de confidentialité : privacy@freecrm.com. Divulgations de sécurité : security@freecrm.com. Nous confirmons la réception des rapports dans les 24 heures."
        },
        {
          "heading": "Adresse postale",
          "body": "FreeCRM, Inc. — Communication client — Delaware, USA"
        }
      ]
    },
    "blog": {
      "title": "Le blog FreeCRM",
      "eyebrow": "Blog",
      "description": "Guides pratiques, analyses approfondies des produits et témoignages clients pour les équipes de vente sortantes et les centres d'appels.",
      "intro": "Guides pratiques pour les ventes sortantes, les ventes internes et les opérations de centre d'appels PME — rédigés par l'équipe qui construit FreeCRM.",
      "sections": [
        {
          "heading": "Comment configurer un composeur automatique en 10 minutes",
          "body": "Un guide étape par étape pour provisionner des numéros, importer une liste et exécuter votre première session de numérotation automatique dans FreeCRM."
        },
        {
          "heading": "5 façons dont FreeCRM surpasse HubSpot pour les appels",
          "body": "Voix et SMS intégrés, pas de majoration par minute, gratuit pour toujours — les fonctionnalités d'appel de FreeCRM comparées à Sales Hub de HubSpot."
        },
        {
          "heading": "Des feuilles de calcul à un CRM en un après-midi",
          "body": "Comment importer des contacts, configurer votre pipeline et commencer à suivre les deals sur FreeCRM en un seul après-midi."
        },
        {
          "heading": "Construire un centre d'appels de 50 postes sur FreeCRM",
          "body": "Comment une entreprise de services à domicile a déployé FreeCRM pour 50 agents en deux semaines — y compris l'approvisionnement de numéros locaux et l'enregistrement d'appels."
        }
      ]
    },
    "help": {
      "title": "Centre d'aide",
      "eyebrow": "Ressources",
      "description": "Guides, tutoriels et réponses pour tirer le meilleur parti de FreeCRM.",
      "intro": "Recherchez des guides et des tutoriels, ou naviguez par sujet. Les clients Pro et Enterprise bénéficient également d'un support par e-mail prioritaire.",
      "sections": [
        {
          "heading": "Démarrer",
          "body": "Créez votre compte, invitez votre équipe, importez des contacts et configurez votre pipeline. La plupart des équipes sont opérationnelles en moins d'une heure."
        },
        {
          "heading": "Appels & SMS",
          "body": "Provisionnez des numéros, configurez l'appel en un clic, enregistrez les appels, utilisez le composeur automatique et envoyez des SMS gratuits depuis n'importe quel enregistrement de contact."
        },
        {
          "heading": "Pipelines & transactions",
          "body": "Personnalisez les étapes, créez des rapports et des prévisions, et suivez chaque transaction, du devis à la facture."
        },
        {
          "heading": "Intégrations",
          "body": "Connectez Google, Microsoft 365, QuickBooks, Zapier, DocuSign, Zoom, Google Meet, Zoho Meeting et plus de 6 000 applications. Synchronisez les contacts, le calendrier, les e-mails, les factures et plus encore."
        },
        {
          "heading": "Admin & sécurité",
          "body": "Gérez les utilisateurs, les rôles, le SSO, les journaux d'audit et l'exportation de données. Les clients Enterprise bénéficient également de la provision SCIM et d'un CSM dédié."
        }
      ]
    },
    "apiDocs": {
      "title": "API CRM",
      "eyebrow": "Docs API",
      "description": "Une API REST basée sur des standards pour la capture de leads, la synchronisation de données et l'automatisation des ventes entrantes et du support au sein de FreeCRM.",
      "intro": "Tirez le meilleur parti de votre CRM et automatisez les ventes entrantes et le service client. Nos services web API permettent à votre site web de pousser et de récupérer des données directement dans FreeCRM.",
      "whatHeading": "Ce que vous pouvez faire",
      "capturesHeading": "Capturez les leads de site web",
      "authHeading": "Authentification",
      "webhooksHeading": "Webhooks",
      "rateLimitsHeading": "Limites de débit & SDKs",
      "sections": [
        {
          "heading": "Ce que vous pouvez faire",
          "body": "Connectez des formulaires de site web, acheminez des e-mails entrants, ouvrez des cas de support, capturez des leads et synchronisez des données avec n'importe quel système externe. Nous utilisons une API REST basée sur des standards, facile à intégrer depuis n'importe quelle technologie."
        },
        {
          "heading": "Capturez les leads de site web",
          "body": "Ajoutez notre modèle de code à n'importe quel formulaire web et commencez à capturer des leads de support client et de vente en temps réel, directement dans FreeCRM. Réduisez le bruit des e-mails entrants et ne perdez jamais un lead."
        },
        {
          "heading": "Webhooks",
          "body": "Abonnez-vous aux événements — contact.created, deal.updated, call.completed, sms.received — et transférez-les vers vos propres systèmes en temps réel."
        },
        {
          "heading": "Limites de débit & SDKs",
          "body": "1 000 requêtes / minute sur Pro, 5 000 / minute sur Enterprise. Des SDKs officiels Node et Python sont disponibles ; les bibliothèques communautaires couvrent Ruby, PHP et Go."
        }
      ]
    },
    "community": {
      "title": "Communauté FreeCRM",
      "eyebrow": "Communauté",
      "description": "Connectez-vous avec des milliers d'utilisateurs de FreeCRM, partagez des guides pratiques et obtenez des réponses de l'équipe.",
      "intro": "Rejoignez des milliers d'utilisateurs de FreeCRM qui partagent des guides pratiques, des automatisations et des meilleures pratiques pour les ventes sortantes et les opérations de centre d'appels.",
      "sections": [
        {
          "heading": "Forums",
          "body": "Posez des questions, partagez ce qui fonctionne et apprenez des équipes qui utilisent FreeCRM de toutes tailles — des fondateurs solos aux centres d'appels de 50 postes."
        },
        {
          "heading": "Slack & Discord",
          "body": "Canaux en temps réel pour les utilisateurs avancés, les administrateurs et les développeurs qui créent sur l'API FreeCRM. L'équipe produit y est également présente."
        },
        {
          "heading": "Heures de bureau",
          "body": "Sessions hebdomadaires en direct couvrant l'intégration, la numérotation automatique, les intégrations et les nouvelles fonctionnalités. Gratuites pour tous les clients."
        }
      ]
    },
    "privacy": {
      "title": "Politique de confidentialité",
      "eyebrow": "Légal",
      "description": "Politique de confidentialité de FreeCRM : vos données sont toujours privées et ne sont jamais partagées avec des tiers.",
      "intro": "Vos données sont toujours privées. Nous ne partageons pas les données de nos clients avec des tiers.",
      "sections": [
        {
          "heading": "Collecte et utilisation des informations",
          "body": "Vos données sont 100 % privées. Nous n'utilisons pas les données que vous saisissez dans le système à d'autres fins que le support de votre CRM. Les données que vous saisissez restent la propriété exclusive des personnes et organisations qui les ont saisies. Ces données ne sont jamais partagées avec des organisations externes ou des tiers. En cas d'acquisition, d'insolvabilité, de changement de propriétaire ou de tout changement de structure d'entreprise, nous veillerons toujours à ce que vos données restent privées et non compromises."
        },
        {
          "heading": "Ce que nous collectons",
          "body": "Nous collectons les informations personnellement identifiables nécessaires pour fournir le service : nom, e-mail, téléphone, détails de facturation et les données que vous choisissez de stocker dans votre CRM. Nous collectons des données de journal et d'utilisation pour exploiter, sécuriser et améliorer le service."
        },
        {
          "heading": "Comment nous utilisons les informations",
          "body": "Nous utilisons les informations pour fournir et améliorer le service, communiquer avec vous, fournir un support, sécuriser la plateforme et respecter les obligations légales. Nous ne vendons jamais de données personnelles."
        },
        {
          "heading": "Partage",
          "body": "Nous ne partageons ni ne vendons les informations des utilisateurs. Nous faisons appel à des sous-traitants de confiance (tels que nos fournisseurs de cloud et de téléphonie) strictement pour exploiter le service, dans le cadre de contrats exigeant la confidentialité et la protection des données."
        },
        {
          "heading": "Choix et corrections",
          "body": "Vous pouvez accéder, corriger, exporter ou supprimer vos données à tout moment depuis l'application, ou en contactant privacy@freecrm.com."
        },
        {
          "heading": "Sécurité",
          "body": "FreeCRM fonctionne sur Amazon AWS avec des contrôles de sécurité physique, réseau et d'application standard de l'industrie, un chiffrement en transit et au repos, et une surveillance 24h/24, 7j/7, 365 jours par an."
        },
        {
          "heading": "Contact",
          "body": "Des questions ou des préoccupations ? Envoyez un e-mail à privacy@freecrm.com. Nous vous répondrons dans un délai raisonnable."
        }
      ]
    },
    "terms": {
      "title": "Conditions d'utilisation",
      "eyebrow": "Légal",
      "description": "L'Accord d'abonnement régissant votre utilisation des logiciels et services FreeCRM.",
      "intro": "En utilisant le logiciel FreeCRM, vous acceptez, au nom de l'entité sous licence du logiciel, d'être lié par le présent Contrat d'abonnement.",
      "sections": [
        {
          "heading": "1. Acceptation",
          "body": "En cliquant sur « J'accepte » ou en utilisant le logiciel FreeCRM que vous avez sélectionné, vous acceptez, au nom de l'entité concédant le logiciel (« Société »), que la Société devienne partie au présent Contrat et que vous avez l'autorité d'engager la Société. Si la Société n'est pas d'accord, n'enregistrez pas et n'utilisez pas le logiciel."
        },
        {
          "heading": "2. Description du service",
          "body": "FreeCRM fournit son service sous réserve des présentes Conditions d'utilisation, qui peuvent être mises à jour par nous de temps à autre. La version actuelle est toujours publiée à cette URL. Des fonctionnalités particulières peuvent être soumises à des directives supplémentaires publiées, qui sont incorporées par référence."
        },
        {
          "heading": "3. Votre compte",
          "body": "Vous êtes responsable de maintenir la confidentialité de vos identifiants et de toute activité se produisant sous votre compte. Informez-nous immédiatement de toute utilisation non autorisée."
        },
        {
          "heading": "4. Utilisation acceptable",
          "body": "Vous acceptez de ne pas utiliser FreeCRM pour envoyer du spam, harceler autrui, enfreindre la propriété intellectuelle, distribuer des logiciels malveillants ou violer toute loi applicable. Nous pouvons suspendre les comptes qui enfreignent ces règles."
        },
        {
          "heading": "5. Frais",
          "body": "Le plan Gratuit est proposé sans frais. Les plans Pro et Enterprise sont facturés à l'avance sur une base mensuelle ou annuelle. L'utilisation de la téléphonie (numéros, minutes, SMS) est facturée selon les tarifs affichés."
        },
        {
          "heading": "6. Propriété des données",
          "body": "Vous conservez tous les droits sur les données que vous saisissez dans FreeCRM. Nous les traitons uniquement pour fournir et améliorer le service."
        },
        {
          "heading": "7. Résiliation",
          "body": "Chaque partie peut résilier à tout moment. À la résiliation, vous pouvez exporter vos données ; après une période raisonnable, nous pouvons les supprimer."
        },
        {
          "heading": "8. Exclusions de garantie et limitation de responsabilité",
          "body": "Le service est fourni « tel quel » sans garanties d'aucune sorte. Dans toute la mesure permise par la loi, la responsabilité totale de FreeCRM est limitée aux frais payés au cours des douze mois précédant la réclamation."
        },
        {
          "heading": "9. Droit applicable",
          "body": "Le présent Contrat est régi par les lois de l'État du Delaware, États-Unis, sans égard aux principes de conflits de lois."
        }
      ]
    },
    "security": {
      "title": "Sécurité",
      "eyebrow": "Confiance",
      "description": "Comment FreeCRM assure la sécurité de vos données — construit sur Amazon AWS avec chiffrement, surveillance 24h/24 et 7j/7, et contrôles d'accès stricts.",
      "intro": "Nous prenons la sécurité des données au sérieux. FreeCRM fonctionne sur Amazon AWS avec un objectif de disponibilité de six neufs (99,9999 %) et des ingénieurs en service 24h/24, 7j/7, 365 jours par an.",
      "sections": [
        {
          "heading": "Infrastructure",
          "body": "FreeCRM fonctionne sur Amazon AWS. Nos installations physiques respectent ou dépassent toutes les exigences fédérales et internationales en matière de sécurité des données, de redondance, de reprise d'activité et de continuité."
        },
        {
          "heading": "Chiffrement",
          "body": "Toutes les données sont chiffrées en transit à l'aide de TLS 1.2+ et au repos à l'aide d'AES-256. Les sauvegardes sont chiffrées et stockées sur plusieurs zones de disponibilité."
        },
        {
          "heading": "Contrôles d'accès",
          "body": "Accès basé sur les rôles à l'intérieur de l'application, principe du moindre privilège pour l'accès à la production, et journalisation d'audit des actions administratives. Les clients Enterprise bénéficient du SSO (SAML), de la provision SCIM et de journaux d'audit complets."
        },
        {
          "heading": "Surveillance & réponse",
          "body": "Surveillance 24h/24, 7j/7, 365 jours par an de la plateforme, alertes automatiques et équipe d'ingénieurs de garde. Nous testons régulièrement les sauvegardes et effectuons des exercices périodiques de réponse aux incidents."
        },
        {
          "heading": "Signalement d'une vulnérabilité",
          "body": "Envoyez un e-mail à security@freecrm.com avec les détails. Nous confirmons la réception des rapports dans les 24 heures et récompensons la divulgation responsable."
        }
      ]
    },
    "dpa": {
      "title": "Addendum sur le traitement des données",
      "eyebrow": "Légal",
      "description": "Le DPA régit la manière dont FreeCRM traite les données personnelles pour le compte des clients, en conformité avec le GDPR et les lois similaires.",
      "intro": "Le présent Addendum sur le traitement des données (DPA) complète votre Contrat d'abonnement FreeCRM et régit la manière dont nous traitons les données personnelles en votre nom.",
      "sections": [
        {
          "heading": "1. Rôles",
          "body": "Vous (le Client) êtes le contrôleur des données. FreeCRM agit en tant que processeur de données et ne traite les données personnelles que sur vos instructions documentées, y compris les transferts, sauf si la loi applicable l'exige."
        },
        {
          "heading": "2. Objet et durée",
          "body": "Objet : fourniture du service FreeCRM. Durée : la durée du Contrat plus toute période de rétention post-résiliation."
        },
        {
          "heading": "3. Sous-traitants",
          "body": "Nous engageons des sous-traitants fiables (y compris AWS pour l'hébergement et nos fournisseurs de téléphonie pour la voix et les SMS). Une liste actualisée est disponible sur demande. Nous informons les clients des modifications importantes."
        },
        {
          "heading": "4. Mesures de sécurité",
          "body": "Nous mettons en œuvre des mesures techniques et organisationnelles appropriées au risque, y compris le chiffrement en transit et au repos, les contrôles d'accès, la journalisation, la gestion des vulnérabilités et la formation du personnel."
        },
        {
          "heading": "5. Droits des personnes concernées",
          "body": "Nous vous aidons à satisfaire les demandes des personnes concernées (accès, rectification, effacement, portabilité) à l'aide d'outils intégrés à l'application et, si nécessaire, d'un support."
        },
        {
          "heading": "6. Transferts internationaux",
          "body": "Lorsque des données personnelles sont transférées en dehors de l'EEE, du Royaume-Uni ou de la Suisse, le transfert est régi par les Clauses Contractuelles Types ou un autre mécanisme de transfert légal."
        },
        {
          "heading": "7. Notification de violation",
          "body": "Nous vous informerons sans délai excessif après avoir eu connaissance d'une violation de données personnelles affectant vos données."
        },
        {
          "heading": "8. Audits",
          "body": "Sur préavis raisonnable, nous mettrons à disposition les informations nécessaires pour démontrer la conformité, y compris les rapports d'audit de tiers, le cas échéant."
        }
      ]
    },
    "industriesIndex": {
      "title": "FreeCRM par secteur d'activité",
      "eyebrow": "Secteurs d’activité",
      "description": "Découvrez comment les équipes des centres d'appels, de l'assurance, de l'immobilier, des cabinets dentaires, des services professionnels et plus encore utilisent FreeCRM.",
      "intro": "Modèles et flux de travail pour les secteurs d'activité qui utilisent FreeCRM."
    }
  }
};

export default fr;
